import { useCallback } from 'react';
import type { WorkoutExerciseEntry } from '@jackedlogs/shared';

import type { ConfirmFn } from '../seams';
import type { DraftEntry, LoggerDispatch } from './state/loggerDraft';
import type { WeightUnit } from './state/loggerDraftTypes';
import { draftSetsFromHistoryEntry, isEntryFresh } from './workoutMapping';

// HistoryPanel copy → reducer bridge. Slice #2 (2026-05-28) collapsed the
// 3-button overwrite/append/cancel popup to a 2-button replace/cancel:
// copy never extends the target. Fresh entry (untouched placeholders)
// skips the confirm entirely. Universal rule lives in the reducer.
//
// ⛔ Through the confirm SEAM, not react-native's Alert (RW-25): inside
// packages/ui an Alert is a no-op on react-native-web, so on the web this
// copy silently did nothing whenever the target already had work in it.
export function useCopyHistory({
  entries,
  defaultUnit,
  dispatch,
  confirm,
  t,
}: {
  entries: DraftEntry[];
  defaultUnit: WeightUnit;
  dispatch: LoggerDispatch;
  confirm: ConfirmFn;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): (entryIdx: number, sourceEntry: WorkoutExerciseEntry) => void {
  return useCallback(
    (entryIdx: number, sourceEntry: WorkoutExerciseEntry) => {
      const target = entries[entryIdx];
      if (!target) return;
      const sets = draftSetsFromHistoryEntry(
        sourceEntry,
        defaultUnit,
        target.loadedSides,
      );
      if (sets.length === 0) return;
      if (isEntryFresh(target)) {
        // Untouched placeholder: seed wholesale so the entry grows to
        // source.length (not capped at the lone blank seed by the
        // universal position-fill rule). Same class of split as the
        // add-time prefill in useAddExercise.
        dispatch({ type: 'seed_new_entry_sets', entryIdx, sets });
        return;
      }
      void confirm({
        title: t('mobile.logger.copyDialogTitle', {
          defaultValue: 'Copy session',
        }),
        body: t('mobile.logger.copyDialogBody', {
          defaultValue: 'Replace pending sets or add to existing?',
        }),
        confirmLabel: t('mobile.logger.copyOverwrite', {
          defaultValue: 'Replace',
        }),
        cancelLabel: t('mobile.workouts.cancel', { defaultValue: 'Cancel' }),
      }).then((ok) => {
        if (ok) dispatch({ type: 'copy_history_to_entry', entryIdx, sets });
      });
    },
    [entries, defaultUnit, dispatch, confirm, t],
  );
}
